import type { ReactNode } from "react";
import { Icon } from "../components/Icon";
import { Avatar } from "../shell/Avatar";
import { OwnerEmpty } from "../shell/OwnerEmpty";
import { MarkdownSummary } from "../components/MarkdownText";
import { SettingsDirectory } from "../connections/context";
import { settingsDestinations } from "../connections/directory";
import { conversationHref } from "../connections/navigation";
import type { PanelEntry } from "../lib/api";
import { pendingInteractionPanels, responseConversationHref } from "../shell/ownerShell";
import { AskCard } from "../studio/AskCard";
import { whenWords } from "../studio/words";
import { agentHref, answerAgentApproval, instanceStatus, wakesOnDemand, type AgentInstance } from "./api";
import { RecentChoiceCard } from "./RecentChoices";
import type { ToastMessage } from "./Toast";
import type { AgentReading } from "./useHubReadings";
import "./responses.css";

/** Pending plugin requests remain owned by their Agent and original conversation. */
export function pendingInteractions(reading?: AgentReading): PanelEntry[] {
  if (reading?.state !== "ready" || !reading.boot?.owner?.present) return [];
  return pendingInteractionPanels(reading.boot.panels ?? []);
}

/** Every read that could hold something for the owner has come back, and none did.  The state is one of
 *  them: the requests an Agent's pages raised are read from it. */
function nothingWaits(reading?: AgentReading): boolean {
  return reading?.state === "ready" && reading.approvalsState === "ready" && reading.choicesState === "ready" && !reading.choicesError
    && !reading.pending?.length && !reading.choices?.length && !pendingInteractions(reading).length;
}

export function requestHref(agent: AgentInstance, panel: PanelEntry): string {
  const request = panel.interaction!;
  return agentHref(agent.id, responseConversationHref(request, { request: request.request_id }));
}

/**
 * Everything waiting on the Owner, as one list across Agents: each row is the request, which Agent
 * asked and since when, with the one thing to do about it, each on a card of its own.  What could not
 * be read says so above the list, naming the Agent when there is more than one.
 */
export function OwnerResponses({ agents, readings, onRefresh, onToast }: {
  agents: AgentInstance[]; readings: Record<string, AgentReading>; onRefresh: () => void;
  /** A word about what an answer just did; the page floats it in the corner. */
  onToast: (message: string, tone: ToastMessage["tone"]) => void;
}) {
  const needsResponse = (agent: AgentInstance) => Boolean(readings[agent.id]?.pending?.length || readings[agent.id]?.choices?.length
    || pendingInteractions(readings[agent.id]).length);
  const ordered = [...agents].sort((a, b) => Number(needsResponse(b)) - Number(needsResponse(a)));
  const notices: ReactNode[] = [];
  const rows: ReactNode[] = [];
  const whose = (agent: AgentInstance) => agents.length > 1 ? <strong>{agent.name || "未命名 IP"}</strong> : null;
  for (const agent of ordered) {
    const reading = readings[agent.id];
    const state = reading?.state;
    const note = (key: string, text: string, extra?: ReactNode) =>
      notices.push(<p className="owner-read-notice" role="status" key={`${agent.id}:${key}`}>{whose(agent)}{text}{extra}</p>);
    // A read that failed says so where it failed, with the one way to try again (one for the IP: it reads
    // everything again); the page reads again by itself too.
    const retry = <button type="button" className="btn btn-quiet" onClick={onRefresh}>重试</button>;
    const approvalsFailed = agent.online && reading?.approvalsState === "failed";
    if (!agent.online) {
      // The Hub already knows why no read is running. Browsing this list remains passive.
      note("state", `这个 IP ${instanceStatus(agent).label}，尚未检查待回应事项。`, wakesOnDemand(agent)
        ? <> <a href={agentHref(agent.id)}>打开 IP 后检查</a></> : null);
    } else if (state !== "ready") {
      note("state", state === "failed" ? "暂时没有读到完整状态。" : "正在读取最新状态…",
        state === "failed" && !approvalsFailed ? retry : null);
    }
    if (agent.online && reading?.approvalsState !== "ready") {
      note("approvals", approvalsFailed ? "待审批的操作暂未读到。" : "正在读取待审批的操作…", approvalsFailed ? retry : null);
    }
    if (agent.online && reading?.choicesState === "reading") note("choices", "正在读取最近工作台对话…");
    if (reading?.choicesError) note("choices-error", reading.choicesError, <> <a href={agentHref(agent.id)}>打开 IP</a></>);
    for (const choice of reading?.choices ?? []) {
      rows.push(<li className="owner-card owner-response-item" key={`${agent.id}:choice:${choice.message.id}`}>
        <RecentChoiceCard choice={choice} agentId={agent.id} disabled={!agent.online || reading?.choicesState !== "ready"} onSent={onRefresh} /></li>);
    }
    if (agent.online) for (const panel of pendingInteractions(reading)) {
      const since = whenWords(panel.interaction!.asked_at ?? null);
      rows.push(<li className="owner-card response-row" key={`${agent.id}:request:${panel.interaction!.request_id}`}>
        <Avatar name={agent.name || "未命名 IP"} identity={agent.id} size="lg" />
        <div className="response-row-copy">
          <h3 data-quoted="true"><MarkdownSummary text={panel.title} /></h3>
          <p>{agent.name || "未命名 IP"}{since ? <><span>·</span>{since}</> : null}</p>
          <p>未注明任务</p>
        </div>
        <a className="btn btn-accent" href={requestHref(agent, panel)} aria-label={`${panel.title}，回应`}>回应</a>
      </li>);
    }
    // This agent's own settings directory, so a configuration card says which settings it touches instead
    // of how many; the cards are the Studio's own component and there it is given one (D387).
    for (const card of reading?.pending ?? []) {
      rows.push(<li className="owner-card owner-response-item" key={`${agent.id}:ask:${card.key}`}>
        <SettingsDirectory.Provider value={settingsDestinations(reading?.boot?.panels ?? [])}>
          <AskCard agentId={agent.id} card={card} tools={reading?.tools ?? {}} disabled={reading?.approvalsState !== "ready" || !agent.online || !card.held_by}
            onAnswer={(key, value) => answerAgentApproval(agent.id, key, value)} onToast={onToast} onDone={onRefresh} />
        </SettingsDirectory.Provider>
        <a className="btn btn-quiet" href={agentHref(agent.id, conversationHref(card.session_id, card.owner_conversation))}>{card.owner_conversation === false ? "去工作区处理" : "回到原对话处理"}<Icon name="arrow" size={15} /></a>
      </li>);
    }
  }
  // Said once for the page, and only when every Agent that could hold something has answered with nothing.
  const checked = agents.filter(agent => agent.online && nothingWaits(readings[agent.id]));
  const quiet = !rows.length && agents.length > 0 && checked.length === agents.length;
  const unchecked = agents.length - checked.length;
  return <div className="owner-response-page">
    {notices}
    {rows.length ? <ul className="response-list">{rows}</ul> : null}
    {!rows.length && !quiet && unchecked > 0 ? <p role="status">{checked.length
      ? `已检查的 IP 暂无待办，还有 ${unchecked} 个尚未检查完整。`
      : `还有 ${unchecked} 个 IP 尚未检查完整，暂不能确认是否有待办。`}</p> : null}
    {quiet ? <OwnerEmpty icon="done" action={<a className="btn" href="/#/overview">回到首页</a>}>现在没有要你回应的事</OwnerEmpty> : null}
  </div>;
}
